import { fetcher } from "@/api/swr/fetcher";
import useSWR, { SWRConfiguration } from "swr";
import useSWRImmutable from "swr/immutable";
import { z } from "zod";

type ZSchema = z.AnyZodObject | z.ZodUndefined | z.ZodArray<z.AnyZodObject>;

type SuccessResponse<Schema extends ZSchema> = {
  message: string;
  data: z.infer<Schema>;
};

type ErrorResponse = {
  message: string;
};

type Config<Schema extends ZSchema> = SWRConfiguration<
  SuccessResponse<Schema>,
  ErrorResponse
>;

type Return<Schema extends ZSchema> = ReturnType<
  typeof useSWR<SuccessResponse<Schema>, ErrorResponse>
>;

export function useQuery<Schema extends ZSchema>(
  schema: Schema,
  url: string,
  config?: Config<Schema>,
): Return<Schema> {
  const { data, ...rest } = useSWR<SuccessResponse<Schema>, ErrorResponse>(
    url,
    fetcher,
    config,
  );
  if (!data) return { data, ...rest };
  return {
    data: {
      message: data.message,
      data: data && schema.parse(data.data),
    },
    ...rest,
  };
}

export function useImmutableQuery<Schema extends ZSchema>(
  schema: Schema,
  url: string,
  config?: Config<Schema>,
): Return<Schema> {
  const { data, ...rest } = useSWRImmutable<
    SuccessResponse<Schema>,
    ErrorResponse
  >(url, fetcher, config);
  if (!data) return { data, ...rest };
  return {
    data: {
      message: data.message,
      data: schema.parse(data.data),
    },
    ...rest,
  };
}
